import { showToast } from 'vant';
import {
  ANDROID_QIPAO_PACKAGE,
  buildAndroidIntentCirclePost,
  buildQipaoCirclePostScheme,
} from '../utils/circlePostAppLink';
import { isWeChatBrowser } from '../utils/isWeChatBrowser';
import { navigateToDownloadPage } from '../utils/navigateToDownloadPage';
/**
 * 图片/视频分享页：用 `circle/post?post_id&user_code&type` 进 App，含 WebView 桥与外部深链
 * 使用方需在 `data` 中提供 `circlePostType: 1 | 2`
 */
export default {
  data() {
    return {
      userCode: '',
      _nativeOpenHandlers: null,
      /** 二次确认：即将离开页面 / 打开起泡 */
      showAppLeaveConfirm: false,
    };
  },
  methods: {
    /**
     * 从 `?` 与 hash 上的 query 解析单键
     * @param {string} key
     * @returns {string}
     */
    getUrlQueryValue(key) {
      if (typeof window === 'undefined') return '';
      const values = [];
      try {
        const sp = new URLSearchParams(window.location.search || '');
        const v = sp.get(key);
        if (v) values.push(v);
      } catch (_) {}
      try {
        const hash = String(window.location.hash || '');
        const idx = hash.indexOf('?');
        if (idx >= 0) {
          const sp2 = new URLSearchParams(hash.slice(idx + 1));
          const v2 = sp2.get(key);
          if (v2) values.push(v2);
        }
      } catch (_) {}
      return values.find(Boolean) || '';
    },
    /**
     * 解析 `user_code`（路由与 URL 上）
     */
    resolveUserCode() {
      const fromRoute = this.$route?.query?.user_code;
      const fromUrl = this.getUrlQueryValue('user_code');
      const v = (fromRoute || fromUrl || '').toString();
      this.userCode = v.trim();
    },
    getBridge() {
      if (typeof window === 'undefined') return null;
      return window.QipaoBridge || null;
    },
    /**
     * 是否处于可调用原生 QipaoBridge 的 App 内 WebView
     * @returns {boolean}
     */
    isInAppWebView() {
      const bridge = this.getBridge();
      return !!(
        bridge &&
        typeof bridge.isSupported === 'boolean' &&
        bridge.isSupported &&
        typeof bridge.openVideoDetail === 'function'
      );
    },
    getDownloadPageHref() {
      if (typeof window === 'undefined') return '';
      const u = new URL(window.location.href);
      u.hash = '#/download';
      return u.toString();
    },
    removeNativeOpenListeners() {
      if (typeof document === 'undefined' || !this._nativeOpenHandlers) return;
      const h = this._nativeOpenHandlers;
      if (h.unified) {
        document.removeEventListener('visibilitychange', h.unified);
        window.removeEventListener('pagehide', h.unified);
        window.removeEventListener('blur', h.unified);
      }
      this._nativeOpenHandlers = null;
    },
    /**
     * @param {string} href
     */
    triggerDeeplinkNavigation(href) {
      if (typeof window === 'undefined') return;
      try {
        const a = document.createElement('a');
        a.href = href;
        a.rel = 'nofollow noopener';
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch (_) {
        try {
          window.location.assign(href);
        } catch (e) {
          window.location.href = href;
        }
      }
    },
    /**
     * @param {string} schemeUrl
     */
    tryQipaoSchemeInIframe(schemeUrl) {
      if (typeof document === 'undefined') return;
      const ifr = document.createElement('iframe');
      ifr.setAttribute('aria-hidden', 'true');
      ifr.title = 'open-app';
      ifr.style.cssText = 'position:fixed;left:-9999px;width:1px;height:1px;opacity:0;pointer-events:none;';
      ifr.src = schemeUrl;
      document.body.appendChild(ifr);
      window.setTimeout(() => {
        try {
          ifr.remove();
        } catch (_) {
          if (ifr.parentNode) ifr.parentNode.removeChild(ifr);
        }
      }, 2000);
    },
    tryOpenNativeAppByDeepLink() {
      if (typeof window === 'undefined' || !this.postId) return;

      showToast({ message: '正在打开起泡…', duration: 2000 });
      this.removeNativeOpenListeners();

      const pkg = ANDROID_QIPAO_PACKAGE;
      const isAndroid = /android/i.test(navigator.userAgent || '');
      const p = {
        postId: this.postId,
        userCode: this.userCode,
        type: this.circlePostType,
      };
      const fallbackUrl = this.getDownloadPageHref();
      const androidIntent = buildAndroidIntentCirclePost(p, pkg, fallbackUrl);
      const qipaoUrl = buildQipaoCirclePostScheme(p);

      const goDownload = () => {
        navigateToDownloadPage(this.$router);
      };

      const inWeChat = isWeChatBrowser();
      const androidFallbackMs = inWeChat ? 1800 : 2600;
      const iosFallbackMs = inWeChat ? 1500 : 2000;

      let leftPage = false;
      const onLeave = () => {
        if (leftPage) return;
        leftPage = true;
        this.removeNativeOpenListeners();
      };

      const h = (ev) => {
        if (ev && (ev.type === 'pagehide' || ev.type === 'blur')) {
          onLeave();
          return;
        }
        if (document.hidden || document.visibilityState === 'hidden') onLeave();
      };
      this._nativeOpenHandlers = { unified: h };
      document.addEventListener('visibilitychange', h, { passive: true });
      window.addEventListener('pagehide', h, { passive: true });
      window.addEventListener('blur', h, { passive: true });

      if (isAndroid) {
        this.triggerDeeplinkNavigation(androidIntent);
        window.setTimeout(() => {
          this.tryQipaoSchemeInIframe(qipaoUrl);
        }, 200);
        window.setTimeout(() => {
          this.removeNativeOpenListeners();
          if (!leftPage) {
            if (!inWeChat) {
              showToast({ message: '未检测到 App，可前往下载页安装', duration: 2500 });
            }
            goDownload();
          }
        }, androidFallbackMs);
        return;
      }

      window.location.assign(qipaoUrl);
      window.setTimeout(() => {
        this.removeNativeOpenListeners();
        if (!leftPage) {
          if (!inWeChat) {
            showToast({ message: '若未自动打开，可前往 App Store 或下载页', duration: 2000 });
          }
          goDownload();
        }
      }, iosFallbackMs);
    },
    /**
     * 点击后先弹出二次确认，用户点「允许」再走 {@link confirmOpenInApp}
     */
    onOpenInApp() {
      if (this.useWechatOpenLaunchApp) {
        showToast({ message: '请点击底部按钮打开起泡 App', duration: 2000 });
        return;
      }
      if (!this.postId) {
        showToast('缺少作品信息');
        return;
      }
      this.showAppLeaveConfirm = true;
    },
    /**
     * 用户确认后：App 内走桥接，外站走深链
     */
    confirmOpenInApp() {
      if (!this.postId) return;
      if (this.isInAppWebView()) {
        const bridge = this.getBridge();
        const p = {
          postId: this.postId,
          post_id: this.postId,
          user_code: this.userCode,
          type: this.circlePostType,
          path: 'circle/post',
        };
        bridge
          .openVideoDetail(p)
          .catch((err) => {
            showToast(err?.message || '打开失败，请重试');
          });
        return;
      }
      this.tryOpenNativeAppByDeepLink();
    },
  },
  beforeUnmount() {
    this.removeNativeOpenListeners();
  },
};
